import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { connect } from "react-redux";
import { toast } from "react-toastify";
import { fetchProfile } from "../../../redux/thunks/userProfileThunk";
import { SigninLoader, ExclamationIcon } from "../../../assets/icons/index";
import Constant from "../../../utils/constant";
import { jwtToken } from "../../shared/common/tokenHelper";
import Auth from "../../../utils/auth";

interface LoginContainerProps {
  profile: any;
  setProfile: (token: string) => void;
}

const Login: React.FC<LoginContainerProps> = ({ profile, setProfile }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [flag, setFlag] = useState(false);
  const [input, setInputFlag] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const parameters = location.state as any;

  const getValidReturnUrl = (): string => {
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get("returnUrl");

    // Security: Validate it's a local path (prevents open redirect attacks)
    if (
      returnUrl &&
      returnUrl.startsWith("/") &&
      !returnUrl.startsWith("//") &&
      !returnUrl.includes("://")
    ) {
      return returnUrl;
    }

    return Constant.DASHBOARD;
  };

  const showLoginFailedMessage = async () => {
    const module = await import("react-toastify");
    setInputFlag(true);
    setFlag(false);
    module.toast.error("Please enter valid credentials");
  };

  const checkRole = (access_token) => {
    const user: any = jwtToken(access_token);

    const permittedRoles = [
      Constant.UserRoleId.SuperAdmin,
      Constant.UserRoleId.Employee,
      Constant.UserRoleId.Contractor,
    ];

    return permittedRoles.includes(parseInt(user.roleId));
  };

  const onSubmit = async () => {
    const service = await import("../../../api/login.service");
    setFlag(true);
    service
      .login(email, password)
      .then((response) => {
        if (!response) {
          showLoginFailedMessage();
        } else {
          if (!checkRole(response?.data?.access_token))
            return toast.warning("Unauthorized Access!");

          //Let the auth class handle addition actions needed for a token response
          Auth.handleTokenResponse(response.data);
          const authToken = Auth.getAuthToken();
          setProfile(authToken);
          clearMiniCRMLocalStorage();
          window.location.href = getValidReturnUrl();
        }
      })
      .catch(() => {
        showLoginFailedMessage();
      });
  };

  const clearMiniCRMLocalStorage = () => {
    localStorage.removeItem("miniCRM_usersFields");
    localStorage.removeItem("miniCRM_schoolFields");
    localStorage.removeItem("miniCRM_districtFields");
  };

  const onKeyPress = (e) => {
    if (e.charCode === 13) onSubmit();
  };

  useEffect(() => {
    if (Auth.getAuthToken() === "") {
      // Preserve query params (like returnUrl) when navigating to login
      const queryString = window.location.search;
      if (queryString) {
        navigate("/login" + queryString);
      } else {
        navigate("/login");
      }
    }
  }, []);

  return (
    <>
      <div className="min-h-screen bg-white flex overflow-y-hidden">
        <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
          <div className="mx-auto w-full max-w-sm lg:w-96">
            <div className="mt-10">
              <img
                className="w-auto max-w-[16rem] mb-2"
                src="images/acadamics-dallas.svg"
                alt="Workflow"
              />
              <h2 className="mt-6 text-xl font-light text-gray-900">
                Sign in to your account
              </h2>
            </div>

            <div className="mt-8">
              <div className="mt-6">
                <form className="space-y-6">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Username
                    </label>
                    <div className="mt-1 relative group items-center">
                      <input
                        id="username"
                        name="username"
                        type="input"
                        autoComplete="username"
                        className={`appearance-none border block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm`}
                        onChange={(event) => setEmail(event.target.value)}
                        onKeyPress={onKeyPress}
                      />

                      {input && email.trim() === "" && (
                        <span>
                          <span className="opacity-0 group-hover:opacity-100 bg-white text-red-500 border-red-400 border transition duration-500 rounded-sm max-w-70% p-0.5 pl-2 pr-3 text-xs inline-block absolute top-1/2 transform -translate-y-1/2 right-2 ">
                            Username is required
                          </span>
                          <ExclamationIcon className="w-1 absolute opacity-100 top-1/2 transform -translate-y-1/2 right-3 cursor-pointer"></ExclamationIcon>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label
                      htmlFor="password"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Password
                    </label>
                    <div className="mt-1 relative group items-center">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        className={`appearance-none block w-full px-3 py-2 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-color focus:border-primary-color sm:text-sm`}
                        onChange={(event) => setPassword(event.target.value)}
                        onKeyPress={onKeyPress}
                      />
                      {input && password.trim() === "" && (
                        <span>
                          <span className="opacity-0 group-hover:opacity-100 bg-white text-red-500 border-red-400 border transition duration-500 rounded-sm max-w-70% p-0.5 pl-2 pr-3 text-xs inline-block absolute top-1/2 transform -translate-y-1/2 right-2 ">
                            Password is required
                          </span>
                          <ExclamationIcon className="w-1 absolute opacity-100 top-1/2 transform -translate-y-1/2 right-3 cursor-pointer"></ExclamationIcon>
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <button
                      type="button"
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#041e42] hover:bg-[#003594] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#003594]"
                      onClick={onSubmit}
                    >
                      {flag === true ? (
                        <SigninLoader
                          style={{ marginRight: "10px" }}
                        ></SigninLoader>
                      ) : (
                        ""
                      )}
                      Sign In
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="hidden lg:block relative w-0 flex-1">
          <img
            className="absolute inset-0 h-full w-full object-cover"
            src="https://images.unsplash.com/photo-1644360266788-572e62457a5f?ixlib=rb-1.2.1&ixid=MnwxM%5B%E2%80%A6%5DG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=687&q=80"
            alt=""
          />
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state: any) => {
  return {
    profile: state.profile,
  };
};

const mapDispatchToProps = (dispatch: any) => {
  return {
    setProfile: (token: string) => {
      dispatch(fetchProfile(token));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
